import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { BaseComponent } from '../../models/base/base-component';
import { TabBarItem } from './tab-bar-item';
import { BehaviorSubject, combineLatest, from, Observable, Subject } from 'rxjs';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { StringExtensions } from '../../utils/string.extensions';
import { ResizeObserver } from '@juggle/resize-observer';
import { LoadingShimmerModule } from 'loading-shimmer';
import { RepeatDirective } from '../../directives/repeat.directive';

@Component({
  selector: 'app-tab-bar',
  templateUrl: './tab-bar.component.html',
  styleUrls: ['./tab-bar.component.scss'],
  standalone: true,
  imports: [NgIf, NgFor, NgClass, LoadingShimmerModule, RepeatDirective]
})
export class TabBarComponent extends BaseComponent implements OnChanges {
  @Input() public tabs: TabBarItem[] = [];
  @Input() public skipFragmentAppending: boolean = false;
  @Input() public tabChangeGuard?: () => boolean | Promise<boolean> | Observable<boolean>;
  @Input() public useAbsolutePositionScrolling: boolean = false;
  @Input() public isLoading: boolean = false;
  @Output() public selectedTab = new EventEmitter<TabBarItem>(true);

  @ViewChild('tabBar') tabBar: ElementRef | undefined;
  @ViewChild('buffer') buffer: ElementRef | undefined;

  constructor(private cdr: ChangeDetectorRef, private router: Router) {
    super();
  }
  private activatedRoute = inject(ActivatedRoute);

  public currentSelectedTab: number = 0;
  private _appendFragmentToUrl = new Subject<TabBarItem>();

  private _tabs = new BehaviorSubject<TabBarItem[]>([]);
  public readonly tabs$ = this._tabs as Observable<TabBarItem[]>;

  private renderer = inject(Renderer2);
  private observer = new ResizeObserver(() => {
    if (this.tabBar && this.buffer) {
      const headerHeight = this.tabBar?.nativeElement?.offsetHeight + 'px';
      this.renderer.setStyle(this.buffer?.nativeElement, 'height', headerHeight);
    }
  });

  scrollFadeEndOpacity: string = '1';
  scrollFadeStartOpacity: string = '0';

  private listenToRoute = combineLatest([
    this.activatedRoute.fragment.pipe(distinctUntilChanged()),
    this.tabs$.pipe(filter(tabs => !!tabs?.length)).distinctUniquelyIdentifiableArray()
  ]).subscribeWhileAlive({
    owner: this,
    next: ([fragment, tabs]) => {
      if (this.skipFragmentAppending) return;
      if (!!fragment) {
        const index = tabs.findIndex(tab => StringExtensions.camelize(tab.title) === fragment);
        if (index > -1) {
          this.tabSelected(index);
        }
      }
    }
  });

  private listenToAppendToFrag = this._appendFragmentToUrl
    .pipe(distinctUntilChanged(), takeUntil(this.onDestroy))
    .subscribe(tab => {
      this.appendFragmentToUrl(tab);
    });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tabs) {
      this.setupViews();
      this._tabs.next(this.tabs);
    }
  }

  setupBindings() {
    this.observer.observe(this.tabBar?.nativeElement);
  }

  public destroy(): void {
    super.destroy();
    this.observer.disconnect();
  }

  setupViews() {
    const selected = this.tabs?.findIndex(tab => tab.active);
    if (selected > -1) {
      this.tabSelected(selected);
    } else {
      this.tabSelected(0);
    }
  }

  public onTabClicked(index: number): void {
    if (index === this.currentSelectedTab) {
      return;
    }
    const guardResult$ = this.tabChangeGuard ? this.tabChangeGuard() : true;
    if (guardResult$ === true) {
      this.tabSelected(index);
      return;
    }
    if (guardResult$ === false) {
      return;
    }
    from(guardResult$)
      .pipe(take(1), takeUntil(this.onDestroy))
      .subscribe(confirmed => {
        if (confirmed) {
          this.tabSelected(index);
        }
      });
  }

  public tabSelected(index: number): void {
    if (index === null || index === undefined || index < 0) return;
    this.currentSelectedTab = index;
    this.tabs?.map(t => (t.active = false));
    if (!!this.tabs && this.tabs?.length > this.currentSelectedTab) {
      this.tabs[this.currentSelectedTab].active = true;
      this.selectedTab.emit(this.tabs[this.currentSelectedTab]);
      this._appendFragmentToUrl.next(this.tabs[this.currentSelectedTab]);
    }
    this.cdr.detectChanges();
  }

  private appendFragmentToUrl(tab: TabBarItem) {
    if (!this.skipFragmentAppending) {
      const frag = StringExtensions.camelize(tab.title);
      const navigationExtras: NavigationExtras = { replaceUrl: true, fragment: frag };
      const current = this.router.parseUrl(this.router.url);
      this.router.navigate(current.root.segments, navigationExtras).then();
    }
  }

  onScroll(event: any) {
    const offsetWidth = event.target.offsetWidth;
    const scrollWidth = event.target.scrollWidth;
    const scrollLeft = event.target.scrollLeft;

    const thresholdWidth = 70;
    const leftDistance = event.target.scrollLeft;
    const rightDistance = scrollWidth - offsetWidth - scrollLeft;

    const startFadeOpacity: number =
      leftDistance <= thresholdWidth ? 1 - (thresholdWidth - leftDistance) / thresholdWidth : 1;
    const endFadeOpacity: number =
      rightDistance <= thresholdWidth ? 1 - (thresholdWidth - rightDistance) / thresholdWidth : 1;
    this.scrollFadeStartOpacity = `${startFadeOpacity}`;
    this.scrollFadeEndOpacity = `${endFadeOpacity}`;
  }
}
